"use client";

import {
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H3 } from "@/components/tool-kit/styles";
import { MacroLineFigure } from "@/components/tools/econ-macro/line-figure";
import type { Strings } from "@/components/tools/multiplier-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type FiscalMultipliers,
  fiscalEffect,
  roundTable,
} from "@/lib/tool-kit/econ-macro/multipliers";

const ROUNDS = 10;

/** Negative zero prints as "−0" in some locales. */
const clean = (value: number) => (value === 0 ? 0 : value);

export function FiscalResults({
  multipliers,
  dg,
  dt,
  strings,
}: {
  multipliers: FiscalMultipliers;
  dg: number;
  dt: number;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const n = (value: number) => fmt(clean(value), { maximumFractionDigits: 4 });
  const amount = (value: number) =>
    fmt(clean(value), { maximumFractionDigits: 2 });
  const m = multipliers;
  const effect = fiscalEffect(m, { spendingChange: dg, taxChange: dt });
  const rows = roundTable(effect.firstRound, m.mpc, ROUNDS);
  const last = rows.at(-1);
  const values = {
    mpc: n(m.mpc),
    mps: n(m.mps),
    k: n(m.spending),
    kt: n(m.tax),
    ktr: n(m.transfer),
    kb: n(m.balancedBudget),
    dg: amount(dg),
    dt: amount(dt),
    dy: amount(effect.total),
    first: amount(effect.firstRound),
  };
  const line = "font-mono text-muted-foreground text-sm leading-relaxed";
  return (
    <div className="space-y-5">
      <StatGrid>
        <StatCard
          emphasis
          label={strings.spendingMultiplier}
          value={n(m.spending)}
        />
        <StatCard label={strings.taxMultiplier} value={n(m.tax)} />
        <StatCard label={strings.transferMultiplier} value={n(m.transfer)} />
        <StatCard
          label={strings.balancedMultiplier}
          value={n(m.balancedBudget)}
        />
      </StatGrid>
      <StatGrid>
        <StatCard
          label={strings.incomeFromSpending}
          value={amount(effect.fromSpending)}
        />
        <StatCard
          label={strings.incomeFromTax}
          value={amount(effect.fromTax)}
        />
        <StatCard
          emphasis
          label={strings.incomeTotal}
          value={amount(effect.total)}
        />
      </StatGrid>
      <div className={`${CARD} space-y-1`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <p className={line}>{t(strings.workSpending, values)}</p>
        <p className={line}>{t(strings.workTax, values)}</p>
        <p className={line}>{t(strings.workTransfer, values)}</p>
        <p className={line}>{t(strings.workBalanced, values)}</p>
        <p className={line}>{t(strings.workIncome, values)}</p>
        <p className={line}>{t(strings.workFirstRound, values)}</p>
      </div>
      {last && effect.firstRound !== 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.roundsHeading}</h3>
          <p className="text-muted-foreground text-sm">
            {t(strings.roundsIntro, {
              rounds: fmt(ROUNDS),
              share: fmt(last.share * 100, { maximumFractionDigits: 1 }),
            })}
          </p>
          <DataTable
            head={[
              strings.roundColumn,
              strings.newSpendingColumn,
              strings.cumulativeColumn,
              strings.shareColumn,
            ]}
            rows={rows.map((row) => [
              fmt(row.round),
              amount(row.amount),
              amount(row.cumulative),
              `${fmt(row.share * 100, { maximumFractionDigits: 1 })}%`,
            ])}
          />
          <MacroLineFigure
            description={t(strings.chartDescriptionFiscal, {
              rounds: fmt(ROUNDS),
              total: amount(effect.total),
            })}
            fallbackName="multiplier"
            fmt={fmt}
            id="mc-chart"
            labels={strings}
            names={[strings.seriesCumulative, strings.seriesLimit]}
            numeric
            rows={rows.map((row) => ({
              label: fmt(row.round),
              x: row.round,
              values: [row.cumulative, effect.total],
            }))}
            title={strings.chartTitleFiscal}
            xTitle={strings.chartXFiscal}
            yTitle={strings.chartYFiscal}
          />
        </>
      ) : null}
      <Notice>{strings.assumptionsFiscal}</Notice>
    </div>
  );
}
